
import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import industryMasterService from "../../../services/masters/industryMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── CSS Styles (same as PlantAdd) ──────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",
  
  border: "1px solid #E2E8F0",
  overflow: "hidden",
  mb: 3,
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa",
    height: "38px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F8FAFC" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
  "& .MuiInputLabel-root": { display: "none" },
};

const formFooterSx = {
  padding: "18px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  fontFamily: "'Inter', Arial, sans-serif",
  "&:hover": { background: "#028b19", transform: "translateY(-1px)" },
  "&:active": { transform: "translateY(0)" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
  transition: "all 0.15s ease",
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "40px",
  background: "#E53E3E",
  color: "#ffffff",
  border: "none",
  "&:hover": { background: "#971f16", transform: "translateY(-1px)" },
  fontSize: "14px",
  fontWeight: 600,
  borderRadius: "8px",
};

// ─────────────────────────────────────────────────────────────────────────────

const IndustryAdd = () => {
  const dispatch = useDispatch();
  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  const { handleSubmit } = useForm();
  const urlLocation = useLocation();
  const editInfo = urlLocation.state;
  const editData = editInfo && editInfo;

  const [data, setData] = useState({
    industry_name: editData ? editData.industry_name : "",
    industry_id: editData ? editData.industry_id : '',
  });

  const [successMessage, setSuccessMessage] = useState(null);

  const handleChange = ({ target }) => {
    const value = target.value;
    setData({
      ...data,
      [target.name]: value,
    });
  };

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const onSubmit = async () => {
    const IndustryData = {
      ...data,
      modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      modified_by: editData ? userInfo.user_id : null,
      created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      created_by: editData ? editData.created_by : userInfo.user_id,
      changedUpdatedValue: "edit",
    };
    try {
      if (editData) {
        await industryMasterService.editDeleteIndustry(IndustryData);
      } else {
        await industryMasterService.addIndustry(IndustryData);
      }
      setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      setSuccessMessage("Error occurred");
    }
  };

  return (
    <Box sx={pageSx}>

      {/* Breadcrumb */}
      <Box sx={breadcrumbSx}>
        <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Industry</span>
      </Box>

      {/* Page Header */}
      <Box sx={pageHeaderSx}>
        <Box sx={pageTitleSx}>
          <CategoryIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
          {editData ? "UPDATE" : "CREATE"} INDUSTRY TYPE MASTER
        </Box>
        <Button onClick={handleBack} sx={backBtnSx}>
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
        </Button>
      </Box>

      {/* Main Form Card */}
      <Box sx={formCardSx}>

        {/* Navy Strip Header */}
        <Box sx={sectionHeaderSx}>
          <CategoryIcon sx={{ fontSize: 18, color: "#fff" }} />
          <Typography sx={sectionHeaderTitleSx}>Industry Information</Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={formBodySx}>
            <Grid container spacing={3}>

              {/* Industry Type */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={fieldLabelSx}>
                    Industry Type <span style={{ color: "#E53E3E" }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="text"
                    onChange={handleChange}
                    name="industry_name"
                    value={data.industry_name}
                    required
                    sx={inputSx}
                    placeholder="Enter industry type"
                  />
                </Box>
              </Grid>

            </Grid>
          </Box>

          {/* Form Footer */}
          <Box sx={formFooterSx}>
            <Button sx={cancelBtnSx} onClick={handleBack}>
              Cancel
            </Button>
            <Button type="submit" sx={saveBtnSx}>
              <SaveIcon sx={{ fontSize: 18 }} />
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </form>
      </Box>

      <ReusableSnackbar
        open={!!successMessage}
        onClose={(event, reason) => {
          if (reason !== 'clickaway') setSuccessMessage("");
        }}
        message={successMessage}
        autoHideDuration={2000}
        vertical="top"
        horizontal="right"
      />
    </Box>
  );
};

export default IndustryAdd;